
import { Interval, DateTime, Duration } from 'luxon';
import React from "react";

import * as d3 from 'd3';

import './TimelineWidget.css'
import TimelinePlot from './TimelinePlot.js';
import PartitionWidget from './PartitionWidget.js';
import SelectionInterval from './SelectionInterval';
import {pixelPadTimeline} from './utils.js';

function screenToSVGCoords(canvas, e) {
    // from https://observablehq.com/@danburzo/dragging-a-svg-rectangle-on-the-screen
    // Read the SVG's bounding rectangle...
    let canvasRect = canvas.getBoundingClientRect();
    // ...and transform clientX / clientY to be relative to that rectangle
    return {
        x: e.clientX - canvasRect.x,
        y: e.clientY - canvasRect.y
    }
}

class TimelineWidget extends React.Component {
    constructor(props) {
        super(props);

        this._which_end = null;
        this._delta_x = null;
        this._buffer = 15;
    }

    componentDidMount() {
        this.drawTimeline();
        this.props['setscreenshotfunc'](this.screenshot.bind(this), 'timeline');
    }

    componentDidUpdate() {
        this.drawTimeline();
    }

    wheel(new_intv) {
        if (this.props['ontboundschange'] !== undefined) {
            this.props['ontboundschange'](new_intv);
        }
        this.drawTimeline();
    }

    drawTimeline() {
        const width = this.props['width'] - this.props['margins']['right'] - this.props['margins']['left'];
        const t_bnds_raw = this.props['tbounds'].getIntervals()[0];
        const [earliest_dt, latest_dt] = pixelPadTimeline(width, t_bnds_raw, this.props['timeline_limits'], this._buffer);
        const t_bnds_intv = Interval.fromDateTimes(earliest_dt, latest_dt).intersection(t_bnds_raw);

        const t_scale = d3.scaleUtc()
            .domain([t_bnds_intv.start.toJSDate(),
                     t_bnds_intv.end.toJSDate()])
            .range([this.props['margins']['left'], this.props['width'] - this.props['margins']['right']]);

        const selection_x_start = t_scale(this.props['interval'].start().toJSDate());
        const selection_x_end = t_scale(this.props['interval'].end().toJSDate());

        var selection_box = d3.select('#timeline-select-box');

        // Remove all the stuff that was there
        selection_box.selectAll('*').remove();

        // Add light gray selection box
        selection_box.append('rect')
            .attr('x', selection_x_start)
            .attr('y', this.props['margins']['top'])
            .attr('width', selection_x_end - selection_x_start)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('fill', '#d9dddd')
            .attr('class', 'selection')
            .attr('clip-path', 'url(#axis-face)');


        // Add dark gray left drag handle
        selection_box.append('rect')
            .attr('x', selection_x_start - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('fill', '#a0abab')
            .attr('class', 'handle-left')
            .attr('clip-path', 'url(#axis-face)');

        // Add dark gray right drag handle
        selection_box.append('rect')
            .attr('x', selection_x_end - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('fill', '#a0abab')
            .attr('class', 'handle-right')
            .attr('clip-path', 'url(#axis-face)');

        // The mouse events are handled in the topmost layer, not where stuff is drawn (because the visible handle can be
        //  hidden behind other things.)
        var mouse_layer = d3.select('#timeline-mouse-layer');

        // Remove all the stuff that was in the mouse layer
        mouse_layer.selectAll('*').remove();

        // Add the transparent click box for the left drag handle
        mouse_layer.append('rect')
            .attr('x', selection_x_start - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('class', 'handle-left-mouse')
            .attr('clip-path', 'url(#axis-face)');

        // Add the transparent click box for the right drag handle
        mouse_layer.append('rect')
            .attr('x', selection_x_end - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('class', 'handle-right-mouse')
            .attr('clip-path', 'url(#axis-face)');
    }

    handleDragStart(event) {
        var svg = document.getElementsByTagName('svg')[0];
        var coords_svg = screenToSVGCoords(svg, event);
        var target_x = d3.select(event.target).attr('x')

        if (d3.select(event.target).classed('handle-left-mouse')) {
            this._which_end = 'left';
        }
        else if (d3.select(event.target).classed('handle-right-mouse')) {
            this._which_end = 'right';
        }
        
        if (this._which_end !== null) {
            this._delta_x = coords_svg['x'] - target_x;
        }
        else {
            this._delta_x = coords_svg['x'];
        }
    }

    handleDragMove(event) {
        if (!this.isDraggingHandle() && !this.isDraggingTimeline()) {
            return;
        }

        const width = this.props['width'] - this.props['margins']['right'] - this.props['margins']['left'];
        const t_bnds_raw = this.props['tbounds'].getIntervals()[0];
        const [earliest_dt, latest_dt] = pixelPadTimeline(width, t_bnds_raw, this.props['timeline_limits'], this._buffer);
        const t_bnds_intv = Interval.fromDateTimes(earliest_dt, latest_dt).intersection(t_bnds_raw);

        const t_scale = d3.scaleUtc()
            .domain([t_bnds_intv.start.toJSDate(),
                     t_bnds_intv.end.toJSDate()])
            .range([this.props['margins']['left'], this.props['width'] - this.props['margins']['right']]);

        var svg = document.getElementsByTagName('svg')[0];
        var coords_svg = screenToSVGCoords(svg, event);
        var new_x = coords_svg['x'] - this._delta_x;

        var new_intv_start = null, new_intv_end = null;
        var new_tbounds_start = null, new_tbounds_end = null;

        if (this._which_end == 'left') {
            let selection = d3.select('.selection');
            const old_start = parseFloat(selection.attr('x'));
            const old_end = old_start + parseFloat(selection.attr('width'));

            new_x = Math.min(new_x, old_end - 3 * 4);

            // Figure out what the new interval is
            new_intv_start = DateTime.fromJSDate(t_scale.invert(new_x + 4), {'zone': 'UTC'});
            new_intv_end = DateTime.fromJSDate(t_scale.invert(old_end), {'zone': 'UTC'});
        }
        else if (this._which_end == 'right') {
            let selection = d3.select('.selection');
            const old_start = parseFloat(selection.attr('x'));

            new_x = Math.max(new_x, old_start + 4);

            // Figure out what the new interval is
            new_intv_start = DateTime.fromJSDate(t_scale.invert(old_start), {'zone': 'UTC'});
            new_intv_end = DateTime.fromJSDate(t_scale.invert(new_x + 4), {'zone': 'UTC'});
        }
        else {
            const orig_dt = DateTime.fromJSDate(t_scale.invert(this._delta_x));
            const drag_dt = DateTime.fromJSDate(t_scale.invert(coords_svg['x']));
            const drag_dur = Duration.fromMillis(orig_dt.toMillis() - drag_dt.toMillis());
            new_tbounds_start = t_bnds_intv.start.plus(drag_dur);
            new_tbounds_end = t_bnds_intv.end.plus(drag_dur);

            const t_bnds_dur = t_bnds_intv.toDuration();

            if (new_tbounds_start < earliest_dt) {
                new_tbounds_start = earliest_dt;
                new_tbounds_end = new_tbounds_start.plus(t_bnds_dur);
            }

            if (new_tbounds_end > latest_dt) {
                new_tbounds_end = latest_dt;
                new_tbounds_start = new_tbounds_end.minus(t_bnds_dur);
            }

            this._delta_x = coords_svg['x'];
        }

        if (new_intv_start !== null && new_intv_end !== null) {
            if (new_intv_start < this.props['timeline_limits'].start) {
                new_intv_start = this.props['timeline_limits'].start;
            }

            if (new_intv_end > this.props['timeline_limits'].end) {
                new_intv_end = this.props['timeline_limits'].end;
            }

            let new_intv;
            if (this.props['interval'].isYearly()) {
                new_intv = this.props['interval'].setYearBounds([new_intv_start.year, new_intv_end.year]);
            }
            else {
                new_intv = new SelectionInterval(Interval.fromDateTimes(new_intv_start, new_intv_end));
            }

            if (this.props['onintervalchange'] !== undefined && !new_intv.equals(this.props['interval'])) {
                this.props['onintervalchange'](new_intv);
            }
        }
        else if (new_tbounds_start !== null && new_tbounds_end !== null) {
            if (this.props['ontboundschange'] !== undefined) {
                const new_tbounds = Interval.fromDateTimes(new_tbounds_start, new_tbounds_end);
                this.props['ontboundschange'](new_tbounds);
            }
        }
    }

    handleDragEnd(event) {
        this._delta_x = null;
        this._which_end = null;
    }

    isDraggingHandle() {
        return this._delta_x !== null && this._which_end !== null;
    }

    isDraggingTimeline() {
        return this._delta_x !== null && this._which_end === null;
    }

    screenshot() {
        const dpr = window.devicePixelRatio || 1;

        let canvas = document.createElement('canvas');
        canvas.width = this.props['width'] * dpr;
        canvas.height = this.props['height'] * dpr;

        let ctx = canvas.getContext('2d');
        const svg_rect = this.svg.getBoundingClientRect();

        ctx.save();
        document.querySelectorAll('rect').forEach((rect, irect) => {
            if (rect.classList.length > 0 && rect.classList[0] != 'selection') {
                return
            }
            const screen_rect = rect.getBoundingClientRect();

            let x, y, width, height, fill;
            if (irect > 0) {
                x = screen_rect['x'] * dpr;
                y = (screen_rect['y'] - svg_rect['top']) * dpr;
                width = screen_rect['width'] * dpr;
                height = screen_rect['height'] * dpr;
                fill = rect.getAttribute('fill');
            }
            else {
                x = rect.getAttribute('x') * dpr;
                y = rect.getAttribute('y') * dpr;
                width = rect.getAttribute('width') * dpr;
                height = rect.getAttribute('height') * dpr;
                fill = null;
            }

            ctx.beginPath();
            ctx.rect(x, y, width, height);
            if (irect == 0) {
                ctx.clip();
            }
            else {
                if (fill === null || fill == 'none') {
                    if (fill == 'none') {
                        ctx.restore();
                    }
                    ctx.lineWidth = dpr;
                    ctx.stroke();
                }
                else {
                    ctx.fillStyle = fill;
                    ctx.fill();
                }
            }
        });

        document.querySelectorAll('line').forEach(line => {
            const screen_rect = line.getBoundingClientRect();

            const x1 = (screen_rect['x'] - 0.5) * dpr;
            const y1 = (screen_rect['y'] - svg_rect['top'] - 0.5) * dpr;
            const x2 = x1 + screen_rect['width'] * dpr;
            const y2 = y1 + screen_rect['height'] * dpr;
            const stroke_dash = line.getAttribute('stroke-dasharray').split(',').map(el => el * dpr);

            ctx.beginPath();
            if (stroke_dash !== null) {
                ctx.setLineDash(stroke_dash);
            }
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.lineWidth = dpr;
            ctx.stroke();
        });

        document.querySelectorAll('text').forEach(text => {
            const drawText = (elem, fontfamily) => {
                const screen_rect = elem.getBoundingClientRect();

                const x1 = screen_rect['x'] * dpr;
                const y1 = (screen_rect['y'] - svg_rect['top'] + 1) * dpr;
                const text = elem.childNodes[0].data;
                let fontsize = elem.getAttribute('font-size');
                if (fontfamily === undefined) {
                    fontfamily = elem.getAttribute('font-family');
                }
                let color = elem.getAttribute('fill');
                const transform = elem.getAttribute('transform');

                if (fontsize === null) {
                    fontsize = '1em';
                }
                if (fontsize.match(/em$/)) {
                    fontsize = fontsize.replace(/em$/, '') * 10;
                }
                fontsize *= dpr;

                if (fontfamily === null) {
                    fontfamily = 'sans-serif';
                }

                if (color === null || color == 'currentColor') {
                    color = '#000000';
                }

                let rotation = 0;
                let textalign = 'left';
                if (transform !== null) {
                    const match = transform.match(/rotate\(([\d]+)\)/);
                    if (match) {
                        rotation = match[1];
                    }

                    if (rotation == 270) {
                        textalign = 'right';
                    }
                }

                ctx.save();
                ctx.translate(x1, y1);
                ctx.rotate(rotation * Math.PI / 180);

                ctx.fillStyle = color;
                ctx.font = fontsize + 'px ' + fontfamily;
                ctx.textBaseline = 'top';
                ctx.textAlign = textalign;
                ctx.fillText(text, 0, 0);

                ctx.restore();
            }

            if ('data' in text.childNodes[0]) {
                drawText(text);
            }
            else {
                const fontfamily = text.getAttribute('font-family');
                text.childNodes.forEach(node => drawText(node, fontfamily));
            }
        });

        return canvas.toDataURL();
    }

    render() {
        const width = this.props['width'] - this.props['margins']['right'] - this.props['margins']['left'];
        const height = this.props['height'] - this.props['margins']['bottom'] - this.props['margins']['top'];

        return (
            <div className='timeline-container'>
                <svg id='timeline' ref={el => this.svg = el} 
                    onMouseDown={this.handleDragStart.bind(this)}
                    onMouseMove={this.handleDragMove.bind(this)}
                    onMouseUp={this.handleDragEnd.bind(this)}
                >
                    <clipPath id='axis-face'>
                        <rect 
                            x={this.props['margins']['left']}
                            y={this.props['margins']['top']}
                            width={width}
                            height={height}
                        ></rect>
                    </clipPath>
                    <g id='timeline-select-box'></g>
                    <TimelinePlot
                        t-bounds={this.props['tbounds']}
                        t-limits={this.props['timeline_limits']}
                        t-buffer={this._buffer}
                        x={this.props['margins']['left']}
                        y={this.props['margins']['top']}
                        width={width}
                        height={height}
                        onwheel={this.wheel.bind(this)}
                        tor_coll={this.props['tor_coll']}
                        interval={this.props['interval']}
                        partitioner={this.props['partitioner']}
                        part_select={this.props['part_select']}
                    />
                    <g id='timeline-mouse-layer'></g>
                    <PartitionWidget 
                        partitioner={this.props['partitioner']}
                        x={this.props['margins']['left'] + width + 5}
                        y={this.props['margins']['top']}
                        width={45}
                        height={height}
                    />
                </svg>
            </div>
        );
    }
}

export default TimelineWidget;
